import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Typography,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Archive as ArchiveIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import categoryService from '../../services/categoryService';

const PendingCategoriesList = ({ onActionSuccess, onPreviewCategory }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [previewDialog, setPreviewDialog] = useState({ open: false, category: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, category: null, reason: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, setNotification] = useState({ message: '', severity: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await categoryService.getAllCategoriesAdmin();
      setCategories(data);
    } catch (err) {
      setError('Erreur lors du chargement des catégories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      pending: 'warning',
      rejected: 'error',
      archived: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Approuvée',
      pending: 'En attente',
      rejected: 'Rejetée',
      archived: 'Archivée'
    };
    return labels[status] || status;
  };

  const handleApprove = async (categoryId) => {
    try {
      setActionLoading(true);
      await categoryService.approveCategory(categoryId);
      setNotification({ message: 'Catégorie approuvée', severity: 'success' });
      fetchCategories();
      onActionSuccess?.();
    } catch (err) {
      setNotification({ message: 'Erreur lors de l\'approbation', severity: 'error' });
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (category) => {
    setRejectDialog({ open: true, category, reason: '' });
  };

  const handlePreviewClick = (category) => {
    if (typeof onPreviewCategory === 'function') {
      onPreviewCategory(category);
      return;
    }
    setPreviewDialog({ open: true, category });
  };

  const handleRejectConfirm = async () => {
    if (!rejectDialog.reason.trim()) {
      setNotification({ message: 'Veuillez indiquer une raison', severity: 'warning' });
      return;
    }

    try {
      setActionLoading(true);
      await categoryService.rejectCategory(rejectDialog.category.id, rejectDialog.reason);
      setNotification({ message: 'Catégorie rejetée', severity: 'success' });
      setRejectDialog({ open: false, category: null, reason: '' });
      fetchCategories();
      onActionSuccess?.();
    } catch (err) {
      setNotification({ message: 'Erreur lors du rejet', severity: 'error' });
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchive = async (categoryId) => {
    try {
      setActionLoading(true);
      await categoryService.archiveCategory(categoryId);
      setNotification({ message: 'Catégorie archivée', severity: 'success' });
      fetchCategories();
      onActionSuccess?.();
    } catch (err) {
      setNotification({ message: 'Erreur lors de l\'archivage', severity: 'error' });
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredCategories = statusFilter === 'all'
    ? categories
    : categories.filter(cat => cat.status === statusFilter);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {notification.message && (
        <Alert severity={notification.severity} sx={{ mb: 2 }}>
          {notification.message}
        </Alert>
      )}

      <FormControl sx={{ mb: 3, minWidth: 200 }}>
        <InputLabel>Filtrer par statut</InputLabel>
        <Select
          value={statusFilter}
          label="Filtrer par statut"
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <MenuItem value="all">Tous les statuts</MenuItem>
          <MenuItem value="active">Approuvées</MenuItem>
          <MenuItem value="pending">En attente</MenuItem>
          <MenuItem value="rejected">Rejetées</MenuItem>
          <MenuItem value="archived">Archivées</MenuItem>
        </Select>
      </FormControl>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Nom</strong></TableCell>
              <TableCell><strong>Slug</strong></TableCell>
              <TableCell><strong>Statut</strong></TableCell>
              <TableCell><strong>Proposée par</strong></TableCell>
              <TableCell><strong>Date de création</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    Aucune catégorie trouvée
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCategories.map((category) => (
                <TableRow key={category.id} hover>
                  <TableCell>{category.name}</TableCell>
                  <TableCell><code>{category.slug}</code></TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(category.status)}
                      color={getStatusColor(category.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {category.submittedBy ? 'User' : 'Admin'}
                  </TableCell>
                  <TableCell>
                    {new Date(category.createdAt).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handlePreviewClick(category)}
                        title="Voir"
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                      {category.status === 'pending' && (
                        <>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleApprove(category.id)}
                            disabled={actionLoading}
                            title="Approuver"
                          >
                            <ApproveIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRejectClick(category)}
                            disabled={actionLoading}
                            title="Rejeter"
                          >
                            <RejectIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {category.status !== 'archived' && (
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleArchive(category.id)}
                          disabled={actionLoading}
                          title="Archiver"
                        >
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, category: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Détails de la catégorie
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={1.5}>
            <Typography><strong>Nom:</strong> {previewDialog.category?.name || '-'}</Typography>
            <Typography><strong>Slug:</strong> {previewDialog.category?.slug || '-'}</Typography>
            <Typography><strong>Statut:</strong> {getStatusLabel(previewDialog.category?.status)}</Typography>
            <Typography>
              <strong>Proposée par:</strong> {previewDialog.category?.submittedBy ? 'User' : 'Admin'}
            </Typography>
            <Typography>
              <strong>Date de création:</strong>{' '}
              {previewDialog.category?.createdAt
                ? new Date(previewDialog.category.createdAt).toLocaleDateString('fr-FR')
                : '-'}
            </Typography>
            {previewDialog.category?.description && (
              <Typography><strong>Description:</strong> {previewDialog.category.description}</Typography>
            )}
            {previewDialog.category?.rejectionReason && (
              <Typography color="error"><strong>Motif de rejet:</strong> {previewDialog.category.rejectionReason}</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog({ open: false, category: null })}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, category: null, reason: '' })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Rejeter la catégorie: {rejectDialog.category?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            placeholder="Indiquez la raison du rejet..."
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setRejectDialog({ open: false, category: null, reason: '' })}
            disabled={actionLoading}
          >
            Annuler
          </Button>
          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
            disabled={actionLoading || !rejectDialog.reason.trim()}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Rejeter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingCategoriesList;
